/**
 * ⛔⛔ THE ROW THE REFRESH BUTTON WILL ACTUALLY REBUILD.
 * ====================================================
 * A catalog line can be BACKED BY a research row stored under a different key than the feed
 * category it is named after — that is the whole subject of task-fx7gfghirm. The line now DISPLAYS
 * which key backs it. The button must target THE SAME ROW, or the label makes the wrong inference
 * more confident rather than less.
 *
 * ⛔ THE BUG THIS CLOSES, AND IT WAS INTRODUCED BY THE FIX FOR THE ORIGINAL ONE. The row rendered
 * "research stored under LEAF" while the guard, the confirmation and the POST all used the FEED
 * path. So the operator reasoned about one row and destroyed another — the exact blind choice the
 * PR was opened to end.
 * ⛔⛔ AND THE LEAF-ONLY CASE WAS WORSE THAN THE MISMATCH. When only a leaf-keyed research row
 * exists, a guard that asks about the FULL path finds nothing, concludes "nothing to overwrite",
 * and runs WITH NO CONFIRMATION AT ALL — on a line the screen has just rendered as researched. The
 * confirmation dialog cannot rescue that, because it never opens.
 *
 * ⚠️ THIS DELIBERATELY DOES NOT NORMALISE ANYTHING. Refreshing a leaf-keyed row rewrites THAT ROW;
 * it does not create a second shape, and it does not migrate the first. Whether the leaf rows
 * should exist at all is task-mprebotrwe's question and is not settled here — see the note there
 * on the 2026-04-29-bridge cohort. A rebuild must be honest about what it rebuilds, nothing more.
 */

export interface RefreshTargetInput {
    /** The feed category this line is named after. */
    categoryPath: string;
    /** Whether a research row joined at all. */
    hasResearch: boolean;
    /** The key the joined research row is stored under, when one joined. */
    researchCategoryPath: string | null;
}

/**
 * The category key a Refresh on this line must guard on, confirm about, and POST to.
 *
 * ⛔ FIRST-TIME RESEARCH KEEPS THE FEED PATH. With no research row there is nothing to overwrite
 * and nothing to be consistent with, and the feed path is the row the operator asked to create.
 * That is also the only case where running without a confirmation is correct.
 */
export function refreshTargetPath(cat: RefreshTargetInput): string {
    if (!cat.hasResearch) return cat.categoryPath;
    // A joined row whose key we somehow do not have is NOT a licence to guess the feed path —
    // but it is also not reachable from the current query, which always selects the key alongside
    // the id. Falling back to the displayed path keeps this total; the caller's guard treats an
    // unknown existence as "confirm", so an unexpected null still cannot produce a silent write.
    return cat.researchCategoryPath ?? cat.categoryPath;
}

/**
 * Does this line's displayed backing row differ from the feed category it is named after?
 * Used to decide whether the row needs the extra label at all.
 */
export const isBackedByDifferentKey = (cat: RefreshTargetInput): boolean =>
    !!cat.researchCategoryPath && cat.researchCategoryPath !== cat.categoryPath;

/**
 * ⛔⛔ THE IDENTITY OF A CATALOG LINE, AND IT IS NOT THE CATEGORY PATH.
 *
 * This feature DELIBERATELY makes categoryPath non-unique: one feed category joined to both a
 * leaf-keyed and a full-path-keyed research row produces TWO lines that are the same category and
 * DIFFERENT rows. Every consumer that treated the path as a primary key inherits that change.
 *
 * ⛔ WHAT THAT COST ON THE BATCH TAB, WHICH ACTS ON MANY CATEGORIES AT ONCE: selection keyed on the
 * path meant selecting either duplicate selected BOTH, Select All stored the value twice, and the
 * POST carried the same category twice — SO THE BACKEND COULD RESEARCH IT TWICE. Paid work, twice,
 * on a destructive path, with the leaf/full distinction invisible at the point of choosing.
 * 🔑 THE RULE THIS ENCODES: WHEN YOU CHANGE WHAT A ROW IDENTITY MEANS, EVERY CONSUMER OF THAT LIST
 * INHERITS THE CHANGE — not every consumer on the screen you happen to be editing.
 */
export const rowKey = (cat: RefreshTargetInput): string =>
    `${cat.categoryPath}::${cat.researchCategoryPath ?? 'none'}`;
